/**
 * Centralized sample action utilities
 * This file contains shared functionality for sample management across different components
 */
import { createElement, useState } from 'react';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { CheckCircle, HelpCircle, XCircle } from 'lucide-react';

/**
 * Hook for managing sample data and actions
 */
export function useSampleActions(supabase: any, customerId: string, loadSamplesCallback: () => Promise<void>) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [samples, setSamples] = useState<any[]>([]);
  const [sampleSubstances, setSampleSubstances] = useState<Record<string, any[]>>({});
  const [expandedSample, setExpandedSample] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showReceiveResultModal, setShowReceiveResultModal] = useState(false);
  const [selectedSample, setSelectedSample] = useState<any | null>(null);
  const [selectedSampleId, setSelectedSampleId] = useState<string | null>(null);

  /**
   * Schedule an appointment for a sample
   */
  const handleScheduleAppointment = (sampleId: string) => {
    setSelectedSampleId(sampleId);
    setShowAppointmentModal(true);
  };

  /**
   * Collect a sample
   */
  const handleCollectSample = (sample: any) => {
    setSelectedSample(sample);
    setShowCollectionModal(true);
  };

  /**
   * Send a sample
   */
  const handleSendSample = (sampleId: string) => {
    setSelectedSampleId(sampleId);
    setShowSendModal(true);
  };
  
  /**
   * Receive result for a sample
   */
  const handleReceiveResult = (sampleId: string) => {
    setSelectedSampleId(sampleId);
    setShowReceiveResultModal(true);
  };

  /**
   * Delete a sample
   */
  const handleDeleteSample = async (sampleId: string) => {
    if (!confirm('Möchten Sie diese Probe wirklich löschen?')) return;
    
    setProcessingId(sampleId);
    try {
      // For contract samples, first check if it's linked to a contract
      const { data: contractSample, error: contractSampleError } = await supabase
        .from('contract_samples')
        .select('id, contract_id, position')
        .eq('sample_id', sampleId)
        .maybeSingle();
        
      if (!contractSampleError && contractSample) {
        const { error: deleteError } = await supabase
          .from('contract_samples')
          .delete()
          .eq('id', contractSample.id);
          
        if (deleteError) throw deleteError;

        const { data: samplesToShift, error: shiftLoadError } = await supabase
          .from('contract_samples')
          .select('id, position')
          .eq('contract_id', contractSample.contract_id)
          .gt('position', contractSample.position)
          .order('position');

        if (shiftLoadError) throw shiftLoadError;

        if (samplesToShift && samplesToShift.length > 0) {
          const updates = samplesToShift.map(sample =>
            supabase
              .from('contract_samples')
              .update({ position: sample.position - 1 })
              .eq('id', sample.id)
          );

          const results = await Promise.all(updates);
          results.forEach(({ error }) => {
            if (error) throw error;
          });
        }
      }
      
      // Delete the customer sample
      const { error } = await supabase
        .from('customer_samples')
        .delete()
        .eq('id', sampleId);
        
      if (error) throw error;
      
      // Reload samples after deletion
      await loadSamplesCallback();
    } catch (err) {
      console.error('Error deleting sample:', err);
      setError('Fehler beim Löschen der Probe');
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Mark a sample as no-show
   */
  const handleNoShow = async (sampleId: string, comment: string = '') => {
    setProcessingId(sampleId);
    try {
      const { error } = await supabase
        .from('customer_samples')
        .update({ status: 'nicht_erschienen', comment })
        .eq('id', sampleId);

      if (error) throw error;
      await loadSamplesCallback();
    } catch (err) {
      console.error('Error updating sample status:', err);
      setError('Fehler beim Aktualisieren des Status');
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Reset a sample to scheduled status
   */
  const handleResetSample = async (sampleId: string) => {
    setProcessingId(sampleId);
    try {
      const { error } = await supabase
        .from('customer_samples')
        .update({
          status: 'scheduled',
          appointment_date: null,
          appointment_time: null,
          collection_date: null,
          collection_time: null,
          sent_date: null,
          sent_time: null,
          received_date: null,
          received_time: null,
          result: null
        })
        .eq('id', sampleId);

      if (error) throw error;
      await loadSamplesCallback();
    } catch (err) {
      console.error('Error resetting sample:', err);
      setError('Fehler beim Zurücksetzen der Probe');
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Load sample substances for a list of sample IDs
   */
  const loadSampleSubstances = async (sampleIds: string[]) => {
    try {
      const chunkSize = 100;
      const aggregated: Record<string, any[]> = {};

      for (let i = 0; i < sampleIds.length; i += chunkSize) {
        const chunk = sampleIds.slice(i, i + chunkSize);

        const { data, error } = await supabase
          .from("sample_substance_details")
          .select("id, sample_id, substance_id, substance_name, category_name")
          .in("sample_id", chunk);

        if (error) {
          console.error("Error loading sample substances:", error);
          throw error;
        }

        if (data) {
          data.forEach(substance => {
            if (!aggregated[substance.sample_id]) {
              aggregated[substance.sample_id] = [];
            }

            aggregated[substance.sample_id].push({
              ...substance,
              substance_name: substance.substance_name,
              category_name: substance.category_name,
            });
          });
        }
      }

      setSampleSubstances((prev) => ({
        ...prev,
        ...aggregated,
      }));
      return aggregated;
    } catch (error) {
      console.error("Error loading sample substances:", error);
      return {};
    }
  };

  return {
    loading,
    setLoading,
    error,
    setError,
    samples,
    setSamples,
    sampleSubstances,
    setSampleSubstances,
    expandedSample,
    setExpandedSample,
    processingId,
    setProcessingId,
    showAppointmentModal,
    setShowAppointmentModal,
    showCollectionModal,
    setShowCollectionModal,
    showSendModal,
    setShowSendModal,
    showReceiveResultModal,
    setShowReceiveResultModal,
    selectedSample,
    setSelectedSample,
    selectedSampleId,
    setSelectedSampleId,
    handleScheduleAppointment,
    handleCollectSample,
    handleSendSample,
    handleReceiveResult,
    handleDeleteSample,
    handleNoShow,
    handleResetSample,
    loadSampleSubstances
  };
}

/**
 * Utility functions for sample status display
 */
// Export individual functions for better imports
export const getStatusIcon = (status: string) => {
  switch (status) {
    case 'scheduled':
      return 'Calendar';
    case 'appointed':
      return 'CalendarClock';
    case 'collected':
      return 'Beaker';
    case 'sent':
      return 'Send';
    case 'received':
      return 'FileCheck';
    case 'nicht_erschienen':
      return 'AlertTriangle';
    case 'abgebrochen':
      return 'Ban';
    default:
      return 'TestTube';
  }
};

export const getStatusLabel = (status: string) => {
  switch (status.toLowerCase()) {
    case 'collected':
      return 'Entnommen';
    case 'scheduled':
      return 'Geplant';
    case 'appointed':
      return 'Einbestellt';
    case 'sent':
      return 'Versendet';
    case 'received':
      return 'Befund erhalten';
    case 'nicht_erschienen':
      return 'Nicht erschienen';
    case 'abgebrochen':
      return 'Abgebrochen';
    case 'in_progress':
      return 'In Bearbeitung';
    case 'completed':
      return 'Abgeschlossen';
    case 'rejected':
      return 'Abgelehnt';
    default:
      return status;
  }
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'scheduled':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
    case 'appointed':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
    case 'collected':
      return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
    case 'sent':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300';
    case 'received':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300';
    case 'nicht_erschienen':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
    case 'abgebrochen':
      return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300';
  }
};

export const getStatusBadgeColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'collected':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
    case 'sent':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300';
    case 'received':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
    case 'nicht_erschienen':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
    case 'abgebrochen':
      return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300';
  }
};

export type SampleResultCategory = 'negativ' | 'positiv' | 'nicht_verwertbar' | 'unknown';

const RESULT_LABELS: Record<Exclude<SampleResultCategory, 'unknown'>, string> = {
  negativ: 'Negativ',
  positiv: 'Positiv',
  nicht_verwertbar: 'Nicht verwertbar',
};

type ResultStyle = {
  Icon?: LucideIcon;
  iconClass: string;
  textClass: string;
  badgeClass: string;
};

const RESULT_STYLES: Record<SampleResultCategory, ResultStyle> = {
  negativ: {
    Icon: CheckCircle,
    iconClass: 'h-4 w-4 text-green-500 mr-1',
    textClass: 'text-green-600 dark:text-green-400',
    badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
  },
  positiv: {
    Icon: XCircle,
    iconClass: 'h-4 w-4 text-red-500 mr-1',
    textClass: 'text-red-600 dark:text-red-400',
    badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
  },
  nicht_verwertbar: {
    Icon: HelpCircle,
    iconClass: 'h-4 w-4 text-yellow-500 mr-1',
    textClass: 'text-yellow-600 dark:text-yellow-400',
    badgeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
  },
  unknown: {
    Icon: undefined,
    iconClass: 'h-4 w-4 mr-1 text-gray-500',
    textClass: '',
    badgeClass: 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300',
  },
};

export const parseSampleResult = (result: string | null): { category: SampleResultCategory; label: string | null } => {
  if (!result) {
    return { category: 'unknown', label: null };
  }

  const trimmed = result.trim();
  const cleaned = trimmed
    .replace(/(CheckCircle|XCircle|HelpCircle)/gi, ' ')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const searchSource = `${trimmed} ${cleaned}`.toLowerCase();

  let category: SampleResultCategory = 'unknown';
  if (/nicht\s*verwertbar/.test(searchSource) || searchSource.includes('helpcircle')) {
    category = 'nicht_verwertbar';
  } else if (searchSource.includes('positiv') || searchSource.includes('xcircle')) {
    category = 'positiv';
  } else if (searchSource.includes('negativ') || searchSource.includes('checkcircle')) {
    category = 'negativ';
  }

  if (category === 'unknown') {
    return { category, label: cleaned || trimmed };
  }

  const fallbackLabel = RESULT_LABELS[category];

  if (!cleaned) {
    return { category, label: fallbackLabel };
  }

  if (cleaned.toLowerCase() === fallbackLabel.toLowerCase()) {
    return { category, label: fallbackLabel };
  }

  return { category, label: cleaned };
};

export const getResultLabel = (result: string | null): string | null => {
  const { label } = parseSampleResult(result);
  return label;
};

export const getResultIcon = (result: string | null): ReactNode => {
  const { category } = parseSampleResult(result);
  const style = RESULT_STYLES[category];
  if (!style?.Icon) return null;

  return createElement(style.Icon, { className: style.iconClass });
};

export const getResultColorClass = (result: string | null): string => {
  const { category } = parseSampleResult(result);
  return RESULT_STYLES[category]?.textClass || '';
};

export const getResultBadgeClass = (result: string | null): string => {
  const { category } = parseSampleResult(result);
  return RESULT_STYLES[category]?.badgeClass || RESULT_STYLES.unknown.badgeClass;
};

export const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('de-DE');
};

export const groupSubstancesByCategory = (substances: any[]) => {
  const categorized: Record<string, any[]> = {};
  
  substances.forEach(substance => {
    const category = substance.category_name || 'Andere';
    if (!categorized[category]) {
      categorized[category] = [];
    }
    categorized[category].push(substance);
  });
  
  return categorized;
};

// For backward compatibility, also export as an object
export const sampleStatusUtils = {
  getStatusIcon,
  getStatusLabel,
  getStatusColor,
  getStatusBadgeColor,
  getResultIcon,
  formatDate,
  groupSubstancesByCategory
};
